import { LoadingOutlined } from "@ant-design/icons";
import { Button, Checkbox, Form, Input, message, Spin } from "antd";
import { ReactElement, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { maxLengthPhoneNumber } from "../../../constants/maxLengthInFolders";
import {
  ruleInn,
  rulePhoneLength,
  ruleRequired,
} from "../../../constants/rules/RulesConstantsForms";
import {
  ruleLettersOnly,
  ruleNameMax,
  ruleNameMin,
  rulePatronymicMax,
  rulePatronymicMin,
  ruleSurnameMax,
  ruleSurnameMin,
} from "../../../constants/rules/RulesConstantsUser";
import { ValidateFormErrorMessages } from "../../../constants/validateFormErrorMessages";
import { REGISTER_USER } from "../../../store/slice/authSliceLogin/authSliceLogin";
import { UI_ITOWithoutTrim } from "../../../UI_ITO/UI_ITOInputWithoutTrim/UI_ITOInputWithoutTrim";
import { UI_ITONumericInput } from "../../../UI_ITO/UI_ITONumericInput/UI_ITONumericInput";
import { UI_ITOPhoneInput } from "../../../UI_ITO/UI_ITOPhoneInput/UI_ITOPhoneInput";
import { useThunkActionLoadingState } from "../../../utils/Hooks/useThunkActionLoadingState";
import {
  formPasswordValidator,
  validatePasswordValue,
} from "../helpers/helpers";

import classes from "./RegistrationAgentForm.module.css";

interface FieldType {
  surname: string;
  name: string;
  patronymic: string;
  inn: string;
  email: string;
  phone_number: string;
  password: string;
  password_confirmation: string;
  type?: string;
  agreement: boolean;
}

const initialValues: FieldType = {
  surname: "",
  name: "",
  patronymic: "",
  inn: "",
  email: "",
  phone_number: "",
  password: "",
  password_confirmation: "",
  agreement: false,
};

export function RegistrationAgentForm(): ReactElement {
  const [form] = Form.useForm();
  const [personalDataProcessingAgreement, setPersonalDataProcessingAgreement] =
    useState(false);
  const [privacyPolicyAgreement, setPrivacyPolicyAgreement] = useState(false);
  const [
    isHasFeedbackPasswordConfirmation,
    setIsHasFeedbackPasswordConfirmation,
  ] = useState(false);

  const { isFetching, sendQuery } = useThunkActionLoadingState();

  const [searchParams] = useSearchParams();

  async function onFinish(values: FieldType) {
    if (!personalDataProcessingAgreement) {
      message.error("Необходимо соглашение на обработку персональных данных");
      return;
    }

    if (!privacyPolicyAgreement) {
      message.error("Необходимо соглашение с политикой конфиденциальности");
      return;
    }

    const transformValues = { ...values, email: values.email.toLowerCase() };

    try {
      const referralCode = searchParams.get("referral_code");
      await sendQuery(
        REGISTER_USER({
          ...transformValues,
          referral_code: referralCode ? referralCode : undefined,
        }),
      );
    } catch {}
  }

  const regBtnChild = isFetching ? (
    <Spin
      indicator={<LoadingOutlined className="loader-white" spin />}
      size="small"
    />
  ) : (
    "Зарегистрироваться"
  );

  return (
    <Form
      onFinish={onFinish}
      form={form}
      initialValues={initialValues}
      className={classes.form}
    >
      <Form.Item<FieldType>
        className={classes.form__item}
        name="surname"
        label="Фамилия"
        layout="vertical"
        rules={[ruleRequired, ruleLettersOnly, ruleSurnameMax, ruleSurnameMin]}
      >
        <UI_ITOWithoutTrim size="large" placeholder="Введите фамилию" />
      </Form.Item>

      <Form.Item<FieldType>
        className={classes.form__item}
        name="name"
        layout="vertical"
        label="Имя"
        rules={[ruleRequired, ruleLettersOnly, ruleNameMax, ruleNameMin]}
      >
        <UI_ITOWithoutTrim size="large" placeholder="Введите имя" />
      </Form.Item>

      <Form.Item<FieldType>
        className={classes.form__item}
        name="patronymic"
        layout="vertical"
        label="Отчество"
        rules={[ruleLettersOnly, rulePatronymicMax, rulePatronymicMin]}
      >
        <UI_ITOWithoutTrim size="large" placeholder="Введите отчество" />
      </Form.Item>

      <Form.Item<FieldType>
        className={classes.form__item}
        name="inn"
        layout="vertical"
        label="ИНН"
        rules={[ruleRequired, ruleInn]}
      >
        <UI_ITONumericInput
          size="large"
          type="tel"
          placeholder="Введите ИНН организации"
        />
      </Form.Item>

      <Form.Item<FieldType>
        className={classes.form__item}
        name="email"
        layout="vertical"
        label="Почта"
        rules={[
          ruleRequired,
          {
            type: "email",
            message: ValidateFormErrorMessages["enterCorrectEmail"],
          },
        ]}
      >
        <Input size="large" placeholder="Email" />
      </Form.Item>

      <Form.Item
        className={classes.form__item}
        name="phone_number"
        layout="vertical"
        label="Номер телефона"
        rules={[ruleRequired, rulePhoneLength]}
      >
        <UI_ITOPhoneInput
          placeholder="Введите номер телефона"
          type="tel"
          maxLength={maxLengthPhoneNumber}
        />
      </Form.Item>

      <Form.Item<FieldType>
        className={classes.form__item}
        name="password"
        layout="vertical"
        label="Пароль"
        rules={[
          ruleRequired,
          {
            validator: formPasswordValidator,
          },
        ]}
      >
        <Input.Password
          size="large"
          placeholder="Введите пароль"
          onChange={(e) => {
            setIsHasFeedbackPasswordConfirmation(
              validatePasswordValue(e.target.value).success,
            );
          }}
        />
      </Form.Item>

      <Form.Item<FieldType>
        className={classes.form__item}
        name="password_confirmation"
        layout="vertical"
        label="Повторите пароль"
        dependencies={["password"]}
        hasFeedback={isHasFeedbackPasswordConfirmation}
        rules={[
          ({ getFieldValue }) => ({
            validator(_, value) {
              if (getFieldValue("password") === value) {
                return Promise.resolve();
              }
              return Promise.reject(new Error("Пароли не совпадают"));
            },
          }),
        ]}
      >
        <Input.Password size="large" placeholder="Введите пароль ещё раз" />
      </Form.Item>

      <div className={classes.form__container}>
        <Checkbox
          className={classes.form__checkbox}
          checked={personalDataProcessingAgreement}
          onChange={(e) => {
            setPersonalDataProcessingAgreement(e.target.checked);
          }}
        >
          {"Я даю согласие на обработку "}
          <a href={`/agreementDataProcessing`} target="_blank">
            персональных данных
          </a>
        </Checkbox>

        <Checkbox
          className={classes.form__checkbox}
          checked={privacyPolicyAgreement}
          onChange={(e) => {
            setPrivacyPolicyAgreement(e.target.checked);
          }}
        >
          {"Я согласен с "}
          <a href={`/agreementPrivacyPolicy`} target="_blank">
            политикой конфиденциальности
          </a>
        </Checkbox>
      </div>

      <div className={classes.form__container} inner-layout="free">
        <Button
          className={classes.form__button}
          htmlType="submit"
          disabled={isFetching}
          color="primary"
          variant="solid"
        >
          {regBtnChild}
        </Button>
        <Link
          className={classes.form__button}
          to="/login"
          button-variant="outlined"
        >
          Уже есть аккаунт
        </Link>
      </div>
    </Form>
  );
}
